/*
    Imports
*/
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Collapse,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Grid,
    Stack,
    TextField,
    Typography,
    InputAdornment,
    IconButton,
} from '@mui/material';
import { Form, FormikProvider, useFormik } from 'formik';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import * as Yup from 'yup';
import DialogComponent from 'src/components/misc/alerts/Dialog';
import LoadingFormButton from 'src/components/misc/Buttons/LoadingFormButton';
import OneTimePassword from 'src/components/misc/Forms/OneTimePassword';
import { DateIcon, PasswordIcon, ShowPasswordIcon, HidePasswordIcon } from 'src/config/icons';
import userService from 'src/services/UserServiceClass';
import { Icon } from '@iconify/react';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';

/*
    Main Working
*/

const ActionWithCode = ({ buttonText, onConfirm, title, description, loading }) => {
    const [showDialog, setShowDialog] = useState(false);

    const formik = useFormik({
        initialValues: { code: '' },
        validationSchema: Yup.object().shape({
            code: Yup.string().required('Code is required').length(6, 'Must be 6 digits'),
        }),
        onSubmit: (values, { setSubmitting, setFieldError }) => {
            onConfirm(values.code)
                .then(() => {
                    setShowDialog(false);
                })
                .catch((err) => {
                    setFieldError('code', err.response?.data?.msg || 'An error occurred.');
                })
                .finally(() => setSubmitting(false));
        },
    });

    return (
        <>
            <LoadingFormButton
                size="large"
                type="button"
                variant="outlined"
                loading={loading}
                onClick={() => setShowDialog(true)}
                sx={{ p: '0px 37px', mr: 2 }}
            >
                {buttonText}
            </LoadingFormButton>
            <Dialog open={showDialog} onClose={() => setShowDialog(false)}>
                <DialogTitle>{title}</DialogTitle>
                <DialogContent>
                    <DialogContentText>{description}</DialogContentText>
                    <FormikProvider value={formik}>
                        <Form onSubmit={formik.handleSubmit}>
                            <OneTimePassword
                                onChange={(val) => formik.setFieldValue('code', val)}
                                error={Boolean(formik.touched.code && formik.errors.code)}
                                helperText={formik.touched.code && formik.errors.code}
                            />
                            <LoadingFormButton
                                type="submit"
                                variant="contained"
                                loading={formik.isSubmitting}
                            >
                                Confirm
                            </LoadingFormButton>
                        </Form>
                    </FormikProvider>
                </DialogContent>
            </Dialog>
        </>
    );
};

const SetupDialog = ({
    open,
    onClose,
    setupData,
    setupFormik,
    confirmFormik,
    setupServerError,
}) => {
    const [showPassword, setShowPassword] = useState(false);

    const handleShowPassword = () => {
        setShowPassword((show) => !show);
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            aria-labelledby="2fa-setup-dialog-title"
            maxWidth="md"
            fullWidth
        >
            <DialogTitle id="2fa-setup-dialog-title">Enable Two-Factor Authentication</DialogTitle>
            <DialogContent>
                {!setupData ? (
                    <>
                        <Typography variant="body2" color="text.secondary" paragraph sx={{ mt: 2 }}>
                            Please verify your identity by providing your current password and date
                            of birth to enable Two-Factor Authentication.
                        </Typography>
                        <FormikProvider value={setupFormik}>
                            <Form autoComplete="off" noValidate onSubmit={setupFormik.handleSubmit}>
                                <Grid container spacing={2} sx={{ mt: 1 }}>
                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            fullWidth
                                            name="password"
                                            label="Password"
                                            type={showPassword ? 'text' : 'password'}
                                            {...setupFormik.getFieldProps('password')}
                                            error={Boolean(
                                                setupFormik.touched.password &&
                                                    setupFormik.errors.password,
                                            )}
                                            helperText={
                                                setupFormik.touched.password &&
                                                setupFormik.errors.password
                                            }
                                            InputProps={{
                                                endAdornment: (
                                                    <InputAdornment position="end">
                                                        <IconButton
                                                            onClick={handleShowPassword}
                                                            edge="end"
                                                        >
                                                            <Icon
                                                                icon={
                                                                    showPassword
                                                                        ? HidePasswordIcon
                                                                        : ShowPasswordIcon
                                                                }
                                                            />
                                                        </IconButton>
                                                    </InputAdornment>
                                                ),
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <LocalizationProvider dateAdapter={AdapterDateFns}>
                                            <DatePicker
                                                label="Date of Birth"
                                                value={setupFormik.values.dob}
                                                onChange={(date) =>
                                                    setupFormik.setFieldValue('dob', date)
                                                }
                                                renderInput={(params) => (
                                                    <TextField
                                                        {...params}
                                                        fullWidth
                                                        error={
                                                            setupFormik.touched.dob &&
                                                            Boolean(setupFormik.errors.dob)
                                                        }
                                                        helperText={
                                                            setupFormik.touched.dob &&
                                                            setupFormik.errors.dob
                                                        }
                                                    />
                                                )}
                                            />
                                        </LocalizationProvider>
                                    </Grid>
                                </Grid>
                            </Form>
                        </FormikProvider>
                        {setupServerError && (
                            <Stack sx={{}} marginTop={3}>
                                <Collapse in={!!setupServerError}>
                                    <Alert severity="error">{setupServerError}</Alert>
                                </Collapse>
                            </Stack>
                        )}
                    </>
                ) : (
                    <Box>
                        <Typography variant="body1" sx={{ mb: 2 }}>
                            Scan the QR code with your authenticator app.
                        </Typography>
                        <img src={setupData.qrCode} alt="2FA QR Code" />
                        <Typography variant="body2" sx={{ mt: 2 }}>
                            Or enter this secret key manually:
                        </Typography>
                        <Typography fontFamily="monospace" my={1}>
                            {setupData.secret}
                        </Typography>
                        {/* <Typography color="error" fontWeight="bold">
                            Save your backup codes securely. You will need them to recover your
                            account.
                        </Typography>
                        <pre>{setupData.backupCodes.join('\n')}</pre> */}
                        <Typography variant="body2" color="text.secondary" paragraph sx={{ mt: 2 }}>
                            Enter the code from your authenticator app to complete the setup.
                        </Typography>
                        <FormikProvider value={confirmFormik}>
                            <Form
                                autoComplete="off"
                                noValidate
                                onSubmit={confirmFormik.handleSubmit}
                            >
                                <OneTimePassword
                                    onChange={(val) => confirmFormik.setFieldValue('code', val)}
                                    error={Boolean(
                                        confirmFormik.touched.code && confirmFormik.errors.code,
                                    )}
                                    helperText={
                                        confirmFormik.touched.code && confirmFormik.errors.code
                                    }
                                />
                            </Form>
                        </FormikProvider>
                    </Box>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                {!setupData ? (
                    <LoadingFormButton
                        type="button"
                        variant="contained"
                        loading={setupFormik.isSubmitting}
                        onClick={() => setupFormik.handleSubmit()}
                    >
                        Continue
                    </LoadingFormButton>
                ) : (
                    <LoadingFormButton
                        type="button"
                        variant="contained"
                        loading={confirmFormik.isSubmitting}
                        onClick={() => confirmFormik.handleSubmit()}
                    >
                        Confirm & Enable
                    </LoadingFormButton>
                )}
            </DialogActions>
        </Dialog>
    );
};

export default ({ user }) => {
    /*
      States, Params, Navigation, Query.
    */
    const [serverError, setServerError] = useState('');
    const [usernameSent, setUsernameSent] = useState(false);
    const [submit, setSubmit] = useState(false);
    const [openDia, setOpenDia] = useState(false);

    // 2FA States
    const [status, setStatus] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [showSetupDialog, setShowSetupDialog] = useState(false);
    const [setupData, setSetupData] = useState();
    const [showBackupCodes, setShowBackupCodes] = useState(false);
    const [backupCodes, setBackupCodes] = useState([]);
    const [disableLoading, setDisableLoading] = useState(false);
    const [regenerateLoading, setRegenerateLoading] = useState(false);
    const [setupServerError, setSetupServerError] = useState('');
    const [success, setSuccess] = useState('');
    const [showSuccessDialog, setShowSuccessDialog] = useState(false);

    const navigate = useNavigate();

    /*
      Form Setup
    */

    const forgetPassword = () => {
        setServerError('');
        setSubmit(true);
        userService
            .forgetPassword({ username: user.username })
            .then((res) => {
                setUsernameSent(user.username);
                setOpenDia(true);
            })
            .catch((err) => {
                console.error('Error Reset Button', err);
                if (!err.response) {
                    setServerError('Error occurred please try later');
                } else {
                    setServerError(err.response.data.msg);
                }
            })
            .finally(() => setSubmit(false));
    };

    const handleClose = () => {
        setOpenDia(false);
    };

    // 2FA Handlers
    const fetchStatus = () => {
        setLoading(true);
        setError('');
        userService
            .get2faStatus()
            .then((data) => {
                setStatus(data);
            })
            .catch(() => setError('Failed to fetch 2FA status.'))
            .finally(() => setLoading(false));
    };

    useEffect(fetchStatus, []);

    const handleDisable = (code) => {
        setDisableLoading(true);
        return userService
            .disable2fa(code)
            .then(() => {
                fetchStatus();
            })
            .finally(() => setDisableLoading(false));
    };

    const handleGenerateNewBackupCodes = (code) => {
        setRegenerateLoading(true);
        return userService
            .generateNewBackupCodes(code)
            .then((data) => {
                setBackupCodes(data.backupCodes);
                setShowBackupCodes(true);
            })
            .finally(() => setRegenerateLoading(false));
    };

    const setupFormik = useFormik({
        initialValues: {
            password: '',
            dob: null,
        },
        validationSchema: Yup.object().shape({
            password: Yup.string().required('Password is required'),
            dob: Yup.date().required('Date of Birth is required').nullable(),
        }),
        onSubmit: (values, { setSubmitting, setFieldError }) => {
            setSetupServerError('');
            userService
                .setup2fa(
                    values.password,
                    values.dob
                        ? `${values.dob.getFullYear()}-${
                              values.dob.getMonth() + 1
                          }-${values.dob.getDate()}`
                        : '',
                )
                .then((data) => {
                    console.log(data);
                    setSetupData(data);
                })
                .catch((err) => {
                    setSetupServerError(err.response?.data?.msg || 'Failed to start 2FA setup.');
                })
                .finally(() => setSubmitting(false));
        },
    });

    const confirmFormik = useFormik({
        initialValues: { code: '' },
        validationSchema: Yup.object().shape({
            code: Yup.string().required('Code is required').length(6, 'Must be 6 digits'),
        }),
        onSubmit: (values, { setSubmitting, setFieldError, resetForm }) => {
            userService
                .confirm2fa(values.code)
                .then(() => {
                    handleSetupDialogClose();
                    setShowSuccessDialog(true);
                    fetchStatus();
                    resetForm();
                })
                .catch((err) => {
                    setFieldError('code', err.response?.data?.msg || 'An error occurred.');
                })
                .finally(() => setSubmitting(false));
        },
    });

    const handleSetupDialogClose = () => {
        setShowSetupDialog(false);
        setSetupData(null);
        setupFormik.resetForm();
        confirmFormik.resetForm();
    };

    const renderEnabledView = () => (
        <Box>
            <ActionWithCode
                buttonText="Disable 2FA"
                onConfirm={handleDisable}
                title="Disable Two-Factor Authentication"
                description="Enter a code from your authenticator app to disable 2FA."
                loading={disableLoading}
            />
            {/* <ActionWithCode
                buttonText="Regenerate Backup Codes"
                onConfirm={handleGenerateNewBackupCodes}
                title="Regenerate Backup Codes"
                description="Enter a code from your authenticator app to generate new backup codes."
                loading={regenerateLoading}
            /> */}
        </Box>
    );

    const render2FA = () => {
        if (loading) return <CircularProgress />;
        if (error) return <Alert severity="error">{error}</Alert>;

        return (
            <>
                {status && status.enabled ? (
                    renderEnabledView()
                ) : (
                    <LoadingFormButton
                        size="large"
                        type="button"
                        variant="outlined"
                        onClick={() => setShowSetupDialog(true)}
                        sx={{ p: '0px 37px' }}
                    >
                        Enable Two Factor Authentication
                    </LoadingFormButton>
                )}
            </>
        );
    };

    return (
        <>
            <Grid container>
                <Grid item xs={12} md={12} sx={{ marginTop: 3, marginBottom: 4 }}>
                    <Typography variant="h6">Change Password</Typography>
                </Grid>
            </Grid>

            <Grid container spacing={0} sx={{ marginTop: 2 }}>
                <Grid item xs={12} md={6}>
                    <LoadingFormButton
                        size="large"
                        type="button"
                        variant="outlined"
                        loading={submit}
                        onClick={forgetPassword}
                        sx={{ p: '0px 37px' }}
                    >
                        Password Change
                    </LoadingFormButton>
                </Grid>
                <Grid item xs={12} sm={12} md={3}>
                    <DialogComponent
                        buttonText={'Close'}
                        openDialog={openDia}
                        handleButton={handleClose}
                        style={{ color: 'white' }}
                    >
                        Check your email inbox to proceed with the password change.
                    </DialogComponent>
                </Grid>
            </Grid>
            <Grid container>
                <Grid item xs={12} md={12} sx={{ marginTop: 3, marginBottom: 4 }}>
                    <Typography variant="h6">Two Factor Authentication</Typography>
                </Grid>
            </Grid>

            <Grid container spacing={0} sx={{ marginTop: 2 }}>
                <Grid item xs={12} md={12}>
                    {render2FA()}
                </Grid>
            </Grid>

            {serverError && (
                <Stack sx={{}} marginTop={3}>
                    <Collapse in={!!serverError}>
                        <Alert severity="error">{serverError}</Alert>
                    </Collapse>
                </Stack>
            )}
            <SetupDialog
                open={showSetupDialog}
                onClose={handleSetupDialogClose}
                setupData={setupData}
                setupFormik={setupFormik}
                confirmFormik={confirmFormik}
                setupServerError={setupServerError}
            />
            <Dialog open={showBackupCodes} onClose={() => setShowBackupCodes(false)}>
                <DialogTitle>New Backup Codes</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Save these new backup codes securely. They will not be shown again.
                    </DialogContentText>
                    <pre>{backupCodes.join('\n')}</pre>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setShowBackupCodes(false)}>Close</Button>
                </DialogActions>
            </Dialog>
            <Dialog open={showSuccessDialog}>
                <DialogTitle>Success!</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Two-factor authentication has been enabled successfully. For your security,
                        you will now be logged out and asked to log in again.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => {
                            setShowSuccessDialog(false);
                            userService.logout();
                            navigate('/login');
                        }}
                    >
                        OK
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};
